// Frozen copy of main's code for the document_generation_v4_enabled / agency_brand_design_enabled flag-off path; delete with the flag.
import { Node } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';

// --- Frame layout (preserve template size/shape, adaptive labels) ---

export type PlaceholderFrame = {
  frameClass?: string;
  frameStyle?: string;
};

export const hasCustomPlaceholderFrame = (frame: PlaceholderFrame): boolean =>
  Boolean(frame.frameClass || frame.frameStyle);

export const extractPlaceholderFrameFromDiv = (
  nodeAttrs: Record<string, unknown>,
  el: HTMLElement
): PlaceholderFrame => {
  const frameClass = String(nodeAttrs.class ?? el.className ?? '').trim() || undefined;
  const frameStyle = String(nodeAttrs.style ?? el.getAttribute('style') ?? '').trim() || undefined;
  return {
    ...(frameClass ? { frameClass } : {}),
    ...(frameStyle ? { frameStyle } : {})
  };
};

export const extractPlaceholderFrameFromImage = (
  attrs: Record<string, unknown>
): PlaceholderFrame => {
  const parts: string[] = [];
  const width = attrs.width;
  const height = attrs.height;
  if (width) {
    parts.push(`width: ${/^\d+$/.test(String(width)) ? `${width}px` : String(width)}`);
  }
  if (height) {
    parts.push(`height: ${/^\d+$/.test(String(height)) ? `${height}px` : String(height)}`);
  }
  const style = attrs.style;
  if (style) parts.push(String(style));
  return parts.length ? { frameStyle: parts.join('; ') } : {};
};

export const parseFrameDimensions = (
  frameStyle?: string | null
): { width?: string; height?: string } => {
  if (!frameStyle) return {};
  const width = frameStyle.match(/(?:^|;)\s*width:\s*([^;]+)/i)?.[1]?.trim();
  const height = frameStyle.match(/(?:^|;)\s*height:\s*([^;]+)/i)?.[1]?.trim();
  return {
    ...(width ? { width } : {}),
    ...(height ? { height } : {})
  };
};

const parseBorderRadius = (frameStyle?: string | null): string | null =>
  frameStyle?.match(/(?:^|;)\s*border-radius:\s*([^;]+)/i)?.[1]?.trim() ?? null;

const parsePx = (value?: string): number | null => {
  if (!value) return null;
  const match = value.match(/^([\d.]+)px$/i);
  return match ? parseFloat(match[1]) : null;
};

export const getPlaceholderMinDimension = (frameStyle?: string | null): number | null => {
  const { width, height } = parseFrameDimensions(frameStyle);
  const w = parsePx(width);
  const h = parsePx(height);
  if (w != null && h != null) return Math.min(w, h);
  return w ?? h;
};

export const isCircularPlaceholderFrame = (frame: PlaceholderFrame): boolean => {
  if (/\brounded-full\b/i.test(frame.frameClass ?? '')) return true;
  const radius = parseBorderRadius(frame.frameStyle);
  if (!radius) return false;
  if (radius === '50%') return true;
  const minDim = getPlaceholderMinDimension(frame.frameStyle);
  const radiusPx = parsePx(radius);
  return minDim != null && radiusPx != null && radiusPx >= minDim / 2 - 1;
};

export type PlaceholderFrameLayout = 'circle' | 'compact' | 'standard';

export const getPlaceholderFrameLayout = (frame: PlaceholderFrame): PlaceholderFrameLayout => {
  if (isCircularPlaceholderFrame(frame)) return 'circle';
  const minDim = getPlaceholderMinDimension(frame.frameStyle);
  if (minDim != null && minDim < 96) return 'compact';
  return 'standard';
};

export const applyAdaptivePlaceholderLabel = (
  container: HTMLElement,
  label: HTMLElement,
  frame: PlaceholderFrame,
  text: string
) => {
  label.textContent = text;
  label.style.textAlign = 'center';
  label.style.lineHeight = '1.15';
  label.style.margin = '0';
  label.style.pointerEvents = 'none';
  label.style.wordBreak = 'break-word';
  label.style.hyphens = 'auto';

  const layout = getPlaceholderFrameLayout(frame);
  const measuredMin = Math.min(container.clientWidth, container.clientHeight);
  const minDim =
    measuredMin > 0 ? measuredMin : (getPlaceholderMinDimension(frame.frameStyle) ?? 72);

  if (layout === 'circle') {
    const fontSize = Math.max(7, Math.min(11, minDim * 0.17));
    label.style.fontSize = `${fontSize}px`;
    label.style.maxWidth = `${minDim * 0.62}px`;
    label.style.maxHeight = `${minDim * 0.62}px`;
    label.style.overflow = 'hidden';
    label.style.display = '-webkit-box';
    label.style.webkitLineClamp = '2';
    label.style.webkitBoxOrient = 'vertical';
    label.style.color = 'inherit';
    return;
  }

  if (layout === 'compact') {
    const fontSize = Math.max(8, Math.min(12, minDim * 0.14));
    label.style.fontSize = `${fontSize}px`;
    label.style.maxWidth = `${Math.max(minDim - 16, 24)}px`;
    label.style.overflow = 'hidden';
    label.style.textOverflow = 'ellipsis';
    label.style.whiteSpace = 'nowrap';
    label.style.color = 'inherit';
    return;
  }

  label.style.fontSize = '12px';
  label.style.maxWidth = '100%';
  label.style.color = 'inherit';
};

export const formatPlaceholderIdentifier = (id: string): string => {
  if (!id) return 'Image';
  return id
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// --- Load-time conversion (agent HTML → interactive placeholder nodes) ---

const IMAGE_PLACEHOLDER_TOKEN = /^\{\{image:([^}]+)\}\}$/;

export const parseImagePlaceholderToken = (value: string): string | null => {
  const match = value.trim().match(IMAGE_PLACEHOLDER_TOKEN);
  return match ? match[1].trim() : null;
};

export const identifierFromPlaceholderLabel = (text: string): string => {
  const cleaned = text.replace(/[[\]]/g, '').trim();
  if (!cleaned) return 'IMAGE';
  return cleaned.toUpperCase().replace(/\s+/g, '_');
};

export const getVisualDivPlaceholderIdentifier = (el: HTMLElement): string | null => {
  if (el.getAttribute('data-image-placeholder') === 'true') return null;

  const text = el.textContent?.trim() ?? '';
  const className = el.className ?? '';
  const id = el.getAttribute('id') ?? '';
  const meta = `${className} ${id}`;

  const textMatch = text.match(/^\[?\s*(logo|photo|image|picture|headshot|avatar)\s*\]?\s*$/i);
  if (textMatch) {
    return textMatch[1].toUpperCase().replace(/\s+/g, '_');
  }

  if (/\b(logo|photo|image|avatar|headshot)[-_]?placeholder\b/i.test(meta)) {
    const classMatch = meta.match(/\b(logo|photo|image|avatar|headshot)[-_]?placeholder\b/i);
    return classMatch ? classMatch[1].toUpperCase() : 'IMAGE';
  }

  const style = el.getAttribute('style') ?? '';
  const hasDashed = /dashed/i.test(style) || /\bdashed\b/i.test(className);
  if (
    hasDashed &&
    text.length > 0 &&
    text.length <= 30 &&
    /logo|photo|image|picture|avatar|headshot/i.test(text)
  ) {
    return identifierFromPlaceholderLabel(text);
  }

  return null;
};

type PlaceholderReplacement = PlaceholderFrame & {
  from: number;
  to: number;
  identifier: string;
};

const applyPlaceholderReplacements = (editor: Editor, replacements: PlaceholderReplacement[]) => {
  if (replacements.length === 0) return false;

  const { state } = editor;
  const tr = state.tr;
  let modified = false;

  for (let i = replacements.length - 1; i >= 0; i--) {
    const { from, to, identifier, frameClass, frameStyle } = replacements[i];
    try {
      const placeholderNode = editor.schema.nodes.imagePlaceholder.create({
        identifier,
        ...(frameClass ? { frameClass } : {}),
        ...(frameStyle ? { frameStyle } : {})
      });
      tr.replaceWith(from, to, placeholderNode);
      modified = true;
    } catch {
      // Skip replacements that violate schema (e.g. block inside inline context).
    }
  }

  if (modified) {
    editor.view.dispatch(tr);
  }

  return modified;
};

export const convertImagePlaceholdersToNodes = (editor: Editor) => {
  const { doc } = editor.state;
  const replacements: PlaceholderReplacement[] = [];

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;

    const regex = /\{\{image:([^}]+)\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(node.text)) !== null) {
      replacements.push({
        from: pos + match.index,
        to: pos + match.index + match[0].length,
        identifier: match[1].trim()
      });
    }
  });

  return applyPlaceholderReplacements(editor, replacements);
};

export const convertImgSrcPlaceholdersToNodes = (editor: Editor) => {
  const { doc } = editor.state;
  const replacements: PlaceholderReplacement[] = [];

  doc.descendants((node, pos) => {
    if (node.type.name !== 'image') return;
    const identifier = parseImagePlaceholderToken(String(node.attrs.src ?? ''));
    if (!identifier) return;
    replacements.push({
      from: pos,
      to: pos + node.nodeSize,
      identifier,
      ...extractPlaceholderFrameFromImage(node.attrs as Record<string, unknown>)
    });
  });

  return applyPlaceholderReplacements(editor, replacements);
};

export const convertDivPlaceholdersToNodes = (editor: Editor) => {
  const { doc } = editor.state;
  const replacements: PlaceholderReplacement[] = [];

  doc.descendants((node, pos) => {
    if (node.type.name !== 'div') return;

    const nodeDOM = editor.view.nodeDOM(pos);
    if (!nodeDOM || !(nodeDOM instanceof HTMLElement)) return;

    const from = pos;
    const to = pos + node.nodeSize;

    if (nodeDOM.getAttribute('data-image-placeholder') === 'true') {
      replacements.push({
        from,
        to,
        identifier: nodeDOM.getAttribute('data-image-identifier') || 'IMAGE',
        frameClass: nodeDOM.getAttribute('data-frame-class') || undefined,
        frameStyle: nodeDOM.getAttribute('data-frame-style') || undefined
      });
      return;
    }

    const visualIdentifier = getVisualDivPlaceholderIdentifier(nodeDOM);
    if (visualIdentifier) {
      replacements.push({
        from,
        to,
        identifier: visualIdentifier,
        ...extractPlaceholderFrameFromDiv(node.attrs as Record<string, unknown>, nodeDOM)
      });
    }
  });

  return applyPlaceholderReplacements(editor, replacements);
};

/** Convert agent/saved HTML placeholders to interactive nodes after content load. */
export const autoConvertImagePlaceholders = (
  editor: Editor,
  options?: { skipIfFocused?: boolean }
) => {
  setTimeout(() => {
    // The editor can be torn down during the delay (a remount, or the user
    // navigating away): its view is gone and every read below would throw.
    if (editor.isDestroyed) {
      return;
    }
    if (options?.skipIfFocused && editor.isFocused) {
      return;
    }
    convertImagePlaceholdersToNodes(editor);
    convertImgSrcPlaceholdersToNodes(editor);
    convertDivPlaceholdersToNodes(editor);
  }, 100);
};

// Extend TipTap Commands interface
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    imagePlaceholder: {
      /**
       * Insert an image placeholder node
       */
      insertImagePlaceholder: (identifier: string) => ReturnType;
    };
  }
}

export const ImagePlaceholderNode = Node.create({
  name: 'imagePlaceholder',

  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,
  isolating: true, // Prevents cursor from entering the node

  addAttributes() {
    return {
      identifier: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-image-identifier'),
        renderHTML: (attributes) => ({
          'data-image-identifier': attributes.identifier
        })
      },
      frameClass: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-frame-class'),
        renderHTML: (attributes) => {
          if (!attributes.frameClass) return {};
          return { 'data-frame-class': attributes.frameClass };
        }
      },
      frameStyle: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-frame-style'),
        renderHTML: (attributes) => {
          if (!attributes.frameStyle) return {};
          return { 'data-frame-style': attributes.frameStyle };
        }
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-image-placeholder="true"]',
        getAttrs: (element) => {
          // Only parse if it has the exact attribute
          if (element.getAttribute('data-image-placeholder') !== 'true') {
            return false;
          }
          const identifier = element.getAttribute('data-image-identifier') || '';
          return {
            identifier,
            frameClass: element.getAttribute('data-frame-class'),
            frameStyle: element.getAttribute('data-frame-style')
          };
        }
      }
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const customFrame = hasCustomPlaceholderFrame(node.attrs);
    const defaultClass =
      'my-4 p-6 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-900 flex flex-col items-center justify-center gap-2 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors cursor-pointer group';

    return [
      'div',
      {
        ...HTMLAttributes,
        'data-image-placeholder': 'true',
        'data-image-identifier': node.attrs.identifier,
        contenteditable: 'false',
        ...(customFrame && node.attrs.frameClass ? { class: node.attrs.frameClass } : {}),
        ...(customFrame && node.attrs.frameStyle ? { style: node.attrs.frameStyle } : {}),
        ...(!customFrame ? { class: defaultClass } : {})
      },
      [
        'div',
        {
          class:
            'text-neutral-400 dark:text-neutral-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors'
        },
        // Use a placeholder that will be replaced by addNodeView() when parsed
        // This ensures the structure is preserved for parsing
        ''
      ],
      [
        'div',
        {
          class: 'text-sm text-neutral-600 dark:text-neutral-400 font-medium'
        },
        node.attrs.identifier || 'Image Placeholder'
      ],
      [
        'div',
        {
          class: 'text-xs text-neutral-400 dark:text-neutral-500'
        },
        'Drag & drop an image or click to upload'
      ]
    ];
  },

  addCommands() {
    return {
      insertImagePlaceholder:
        (identifier: string) =>
        ({ commands }) =>
          commands.insertContent([
            {
              type: this.name,
              attrs: { identifier }
            },
            {
              type: 'paragraph'
            }
          ])
    };
  },

  addKeyboardShortcuts() {
    return {
      Delete: () => {
        const { state } = this.editor;
        const { selection } = state;

        if (selection instanceof NodeSelection && selection.node.type.name === 'imagePlaceholder') {
          return this.editor.commands.deleteSelection();
        }

        return false;
      },
      Backspace: () => {
        const { state } = this.editor;
        const { selection } = state;

        if (selection instanceof NodeSelection && selection.node.type.name === 'imagePlaceholder') {
          return this.editor.commands.deleteSelection();
        }

        return false;
      }
    };
  },

  addNodeView() {
    const DEFAULT_BOX_CLASS =
      'my-4 relative p-6 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-900 flex flex-col items-center justify-center gap-2 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors cursor-pointer group';
    const INLINE_CHROME_CLASS = 'relative cursor-pointer group overflow-hidden';

    return ({ node, getPos, editor }) => {
      const customFrame = hasCustomPlaceholderFrame(node.attrs);
      const frameLayout = customFrame ? getPlaceholderFrameLayout(node.attrs) : null;

      const container = document.createElement('div');
      container.setAttribute('data-image-placeholder', 'true');
      container.setAttribute('data-image-identifier', node.attrs.identifier);
      container.setAttribute('contenteditable', 'false');
      if (node.attrs.frameClass) {
        container.setAttribute('data-frame-class', node.attrs.frameClass);
      }
      if (node.attrs.frameStyle) {
        container.setAttribute('data-frame-style', node.attrs.frameStyle);
        container.setAttribute('style', node.attrs.frameStyle);
      }

      if (customFrame) {
        container.className = node.attrs.frameClass
          ? `${node.attrs.frameClass} ${INLINE_CHROME_CLASS}`.trim()
          : INLINE_CHROME_CLASS;
      } else {
        container.className = DEFAULT_BOX_CLASS;
      }

      // Remove (X) button — keep out of the way on small/circular frames
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', 'Remove image');
      const compactRemove = customFrame && (frameLayout === 'circle' || frameLayout === 'compact');
      removeBtn.className = compactRemove
        ? 'absolute top-0 right-0 z-20 p-0.5 rounded-full bg-white/90 shadow-sm text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity'
        : 'absolute top-2 right-2 z-20 p-1 rounded-md text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-white/10 transition-colors';
      removeBtn.innerHTML = compactRemove
        ? `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`
        : `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`;
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);
            editor.commands.deleteSelection();
          }
        }
      });
      container.appendChild(removeBtn);

      // Icon container
      const iconContainer = document.createElement('div');
      iconContainer.className = customFrame
        ? 'text-neutral-400 dark:text-neutral-500 pointer-events-none'
        : 'text-neutral-400 dark:text-neutral-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors';
      const iconSize = customFrame ? 'w-6 h-6' : 'w-12 h-12';
      iconContainer.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="${iconSize}">
          <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
        </svg>
      `;

      const formatIdentifier = formatPlaceholderIdentifier;

      // Identifier label
      const label = document.createElement('div');
      label.className = customFrame
        ? ''
        : 'text-sm text-neutral-600 dark:text-neutral-400 font-medium';
      if (!customFrame) {
        label.textContent = node.attrs.identifier || 'Image Placeholder';
      }

      // Instructions
      const instructions = document.createElement('div');
      instructions.textContent = 'Drag & drop an image or click to upload';
      instructions.className = 'text-xs text-neutral-400 dark:text-neutral-500';

      const showEmptyFileError = (fileName: string) => {
        if (customFrame) return;
        instructions.textContent = `"${fileName}" is empty. Please upload a readable image.`;
        instructions.className = 'text-xs text-red-600 dark:text-red-400';
        setTimeout(() => {
          instructions.textContent = 'Drag & drop an image or click to upload';
          instructions.className = 'text-xs text-neutral-400 dark:text-neutral-500';
        }, 4000);
      };

      let contentLayer: HTMLDivElement | null = null;
      const fitCustomLabel = (attrs: typeof node.attrs = node.attrs) => {
        if (!customFrame) return;
        applyAdaptivePlaceholderLabel(container, label, attrs, formatIdentifier(attrs.identifier));
      };

      if (customFrame) {
        contentLayer = document.createElement('div');
        contentLayer.className =
          'absolute inset-0 flex items-center justify-center pointer-events-none';
        if (frameLayout === 'circle') {
          contentLayer.style.padding = '24%';
        } else if (frameLayout === 'compact') {
          contentLayer.style.padding = '6px';
        } else {
          contentLayer.style.padding = '12px';
        }
        contentLayer.appendChild(label);
        container.appendChild(contentLayer);
        requestAnimationFrame(() => fitCustomLabel());
      } else {
        container.append(iconContainer, label, instructions);
      }

      // Handle click to upload and selection
      container.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if ((e.target as HTMLElement).closest('button[aria-label="Remove image"]')) return;
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);
          }
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (!file) return;
          if (file.size === 0) {
            showEmptyFileError(file.name);
            return;
          }
          if (typeof getPos === 'function') {
            const uploadEvent = new CustomEvent('image-placeholder-upload', {
              detail: { file, pos: getPos(), identifier: node.attrs.identifier },
              bubbles: true,
              composed: true
            });
            container.dispatchEvent(uploadEvent);
          }
        };
        input.click();
      });

      // Handle drag and drop
      let dragCounter = 0;

      container.addEventListener('dragenter', (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter++;
        if (!customFrame) {
          container.classList.add('border-primary-500', 'bg-primary-100', 'dark:bg-primary-500/20');
          container.classList.remove(
            'border-neutral-300',
            'dark:border-neutral-600',
            'bg-neutral-50',
            'dark:bg-neutral-900'
          );
        } else {
          container.classList.add('ring-2', 'ring-primary-400');
        }
      });

      container.addEventListener('dragleave', (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter--;
        if (dragCounter === 0) {
          if (!customFrame) {
            container.classList.remove(
              'border-primary-500',
              'bg-primary-100',
              'dark:bg-primary-500/20'
            );
            container.classList.add(
              'border-neutral-300',
              'dark:border-neutral-600',
              'bg-neutral-50',
              'dark:bg-neutral-900'
            );
          } else {
            container.classList.remove('ring-2', 'ring-primary-400');
          }
        }
      });

      container.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });

      container.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter = 0;
        if (!customFrame) {
          container.classList.remove(
            'border-primary-500',
            'bg-primary-100',
            'dark:bg-primary-500/20'
          );
          container.classList.add(
            'border-neutral-300',
            'dark:border-neutral-600',
            'bg-neutral-50',
            'dark:bg-neutral-900'
          );
        } else {
          container.classList.remove('ring-2', 'ring-primary-400');
        }

        const file = e.dataTransfer?.files[0];
        if (!file || !file.type.startsWith('image/') || typeof getPos !== 'function') return;
        if (file.size === 0) {
          showEmptyFileError(file.name);
          return;
        }
        const uploadEvent = new CustomEvent('image-placeholder-upload', {
          detail: { file, pos: getPos(), identifier: node.attrs.identifier },
          bubbles: true,
          composed: true
        });
        container.dispatchEvent(uploadEvent);
      });

      return {
        dom: container,
        contentDOM: null, // Atom nodes don't have content
        update: (updatedNode) => {
          if (updatedNode.attrs.identifier !== node.attrs.identifier) {
            container.setAttribute('data-image-identifier', updatedNode.attrs.identifier);
            if (customFrame) {
              fitCustomLabel(updatedNode.attrs);
            } else {
              label.textContent = updatedNode.attrs.identifier || 'Image Placeholder';
            }
          }
          if (updatedNode.attrs.frameClass !== node.attrs.frameClass) {
            if (updatedNode.attrs.frameClass) {
              container.setAttribute('data-frame-class', updatedNode.attrs.frameClass);
            } else {
              container.removeAttribute('data-frame-class');
            }
          }
          if (updatedNode.attrs.frameStyle !== node.attrs.frameStyle) {
            if (updatedNode.attrs.frameStyle) {
              container.setAttribute('data-frame-style', updatedNode.attrs.frameStyle);
              container.setAttribute('style', updatedNode.attrs.frameStyle);
            } else {
              container.removeAttribute('data-frame-style');
              container.removeAttribute('style');
            }
          }
          return true;
        }
      };
    };
  }
});
